import {
  Component,
  ChangeDetectorRef,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Subject, from, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { MentionSearchService } from './mention-search.service';
import {
  ComposerPart,
  MentionSuggestion,
  findActiveMentionAtCursor,
  findPlainDisplayMentions,
  mergeAdjacentTextParts,
  parseMentionWire,
  partWireLen,
  partsFromWire,
  resolvePlainMentionsInText,
  wireFromParts,
  MENTION_WIRE_TOKEN_RE
} from './mention-composer.util';

// Auto-grow cap for `multiline` composers (AA-228) — 1 row minimum (the textarea's own `rows`
// attribute), grows up to this many before `overflow-y: auto` takes over instead of the field
// pushing the rest of the page around indefinitely.
export const MULTILINE_MAX_ROWS = 9;
// Ceiling on the row-based cap above, as a percentage of the viewport's own height. Without this,
// 12 rows fits fine on a laptop screen but swallows most of a phone's viewport (already fighting
// the on-screen keyboard for space) — this makes the field shrink itself on short viewports
// instead of needing a separate mobile-only breakpoint override.
const MULTILINE_MAX_VH = 40;

// Chrome/Edge (and Safari 17+) auto-grow a `field-sizing: content` textarea with no JS at all; this
// gates the manual scrollHeight fallback for engines that don't support it yet (Firefox, older
// Safari) so the two resizing mechanisms never fight over the element's height.
const supportsFieldSizing = (): boolean =>
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('field-sizing', 'content');

export interface MentionComposerSubmit {
  wire: string;
  /** Human-readable text for chat bubbles. */
  displayText: string;
  /** Message for td-agent-gateway with `@[Name](user:id)` tokens. */
  gatewayMessage: string;
  selection: ReturnType<typeof parseMentionWire>['selection'];
}

/** Fired when a pasted/typed @mention cannot be sent until the user confirms a target. */
export interface MentionComposerClarification {
  reason: 'ambiguous' | 'unresolved';
  label: string;
  wire: string;
  clarification: 'user' | 'team';
  candidates?: MentionSuggestion[];
}

@UntilDestroy()
@Component({
  selector: 'app-mention-composer',
  templateUrl: './mention-composer.component.html',
  styleUrls: ['./mention-composer.component.scss'],
})
export class MentionComposerComponent implements OnInit, OnDestroy {
  @Input() disabled = false;
  @Input() placeholder = '';
  @Input() mentionsEnabled = true;
  @Input() multiline = false;
  @Input() sendIcon = 'arrow_upward';
  /** 'pds' swaps the brand-blue chip for the PDS input chip (light pill, avatar, l size) and puts
   * avatars in the dropdown. Defaults to the original brand chip so existing hosts are unchanged. */
  @Input() appearance: 'brand' | 'pds' = 'brand';
  /** Characters after the `@` before the dropdown opens. 0 (the default) opens it on the bare `@`,
   * which is what the copilot side panel has always done. */
  @Input() mentionMinChars = 0;
  /** Hosts that render their own send control (the Ask AI composers own a send button plus a row
   * of scope chips) set this false and call onSend() themselves. */
  @Input() showSendButton = true;
  /** Whether the empty-state placeholder folds in the Shift+Enter hint (see placeholderWithHint).
   * Ignored when multiline is false. Some hosts' placeholder is already a full sentence describing
   * what to ask, and appending the hint there reads as noise rather than a helpful aside. */
  @Input() showNewLineHint = true;

  @Output() submitComposer = new EventEmitter<MentionComposerSubmit>();
  @Output() wireChange = new EventEmitter<string>();
  @Output() mentionNeedsConfirm = new EventEmitter<MentionComposerClarification>();

  @ViewChildren('composerTextInput') composerTextInputRefs: QueryList<
    ElementRef<HTMLInputElement | HTMLTextAreaElement>
  >;

  readonly multilineMaxRows = MULTILINE_MAX_ROWS;
  // Whichever is smaller: MULTILINE_MAX_ROWS worth of lines, or MULTILINE_MAX_VH of the actual
  // viewport — so the field still fills up to 12 rows on a normal screen but backs off on a short
  // one instead of the two values being fought over by separate CSS rules.
  readonly multilineMaxHeight = `min(${MULTILINE_MAX_ROWS * 1.4}em, ${MULTILINE_MAX_VH}vh)`;
  private readonly fieldSizingSupported = supportsFieldSizing();

  composerParts: ComposerPart[] = [{ kind: 'text', text: '' }];
  mentionSuggestions: MentionSuggestion[] = [];
  isMentionDropdownOpen = false;
  activeMentionIndex = 0;

  private mentionSearch$ = new Subject<string>();
  private mentionRange: { start: number, end: number } | null = null;

  constructor(
    private readonly mentionSearch: MentionSearchService,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
  ) {}

  /** The empty-state placeholder, with the Shift+Enter hint folded in rather than shown as its
   * own line (AA-228 follow-up — a dedicated hint row cost vertical space the user didn't want to
   * spend on it). Only multiline needs the hint at all — a single-line composer can't hold a
   * newline in the first place. */
  get placeholderWithHint(): string {
    if (!this.multiline || !this.showNewLineHint || !this.placeholder) {
      return this.placeholder;
    }
    return `${this.placeholder}, ${this.translate.instant('mentionComposer.newLineHint')}`;
  }

  ngOnInit(): void {
    this.mentionSearch$
      .pipe(
        debounceTime(200),
        switchMap((query) =>
          from(this.mentionSearch.search(query)).pipe(
            map((items) => ({ items, query })),
            catchError(() => of({ items: [], query })),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe(({ items, query }) => {
        if (!this.mentionsEnabled) {
          this.closeMentionDropdown();
          this.cdr.markForCheck();
          return;
        }
        this.mentionSuggestions = items;
        this.activeMentionIndex = 0;
        this.isMentionDropdownOpen = !!this.mentionRange && items.length > 0 && this.hasMinChars(query);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.mentionSearch$.complete();
  }

  get canSend(): boolean {
    return wireFromParts(this.composerParts).trim().length > 0 && !this.disabled;
  }

  clear(): void {
    this.setComposerFromWire('');
    this.closeMentionDropdown();
  }

  /** Restore Ask AI wire tokens into the composer (clarification retry). */
  restoreWire(wire: string): void {
    this.setComposerFromWire(wire ?? '');
  }

  /** Focus the composer and open the @ mention picker. */
  focusForMention(): void {
    const wire = wireFromParts(this.composerParts);
    const active = findActiveMentionAtCursor(wire, wire.length);
    if (!active && !wire.endsWith('@')) {
      const spacer = wire.length > 0 && !/\s$/.test(wire) ? ' ' : '';
      this.setComposerFromWire(`${wire}${spacer}@`);
    }
    this.focus();
    const nextWire = wireFromParts(this.composerParts);
    this.updateMentionContext(nextWire, nextWire.length);
  }

  setWire(wire: string): void {
    this.setComposerFromWire(wire ?? '');
  }

  focus(): void {
    setTimeout(() => {
      const refs = this.composerTextInputRefs?.toArray() ?? [];
      const last = refs[refs.length - 1]?.nativeElement;
      last?.focus();
      const len = last?.value?.length ?? 0;
      last?.setSelectionRange?.(len, len);
    }, 0);
  }

  composerPartTrackBy(index: number): number {
    return index;
  }

  isFirstTextPart(index: number): boolean {
    const p = this.composerParts[index];
    if (!p || p.kind !== 'text') {
      return false;
    }
    for (let k = 0; k < index; k++) {
      if (this.composerParts[k].kind === 'text') {
        return false;
      }
    }
    return true;
  }

  /** Check if composer is completely empty (no text and no mentions) */
  /** True for the empty text spacer partsFromWire inserts before a mention that opens the
   * message (there's no text to its left to type into, so the leading input is inert until
   * clicked). Its box collapses to zero in CSS so a fresh mention-first composer doesn't show a
   * blank gap before the chip; focusing it (to type before the chip) expands it back. */
  isLeadingSpacerBeforeMention(index: number): boolean {
    return index === 0
      && this.composerParts[0]?.kind === 'text'
      && this.composerParts[0].text === ''
      && this.composerParts[1]?.kind === 'mention';
  }

  isComposerEmpty(): boolean {
    return this.composerParts.every(part =>
      part.kind === 'text' ? !part.text.trim() : false,
    );
  }

  onComposerShellMouseDown(event: MouseEvent): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    event.preventDefault();
    this.focus();
  }

  onComposerFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as HTMLElement | null;
    const shell = event.currentTarget as HTMLElement | null;
    if (
      next &&
      (shell?.contains(next) || next.closest?.('.mentions-dropdown'))
    ) {
      return;
    }
    setTimeout(() => this.closeMentionDropdown(), 120);
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (this.isMentionDropdownOpen && this.mentionSuggestions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeMentionIndex = Math.min(
          this.activeMentionIndex + 1,
          this.mentionSuggestions.length - 1,
        );
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeMentionIndex = Math.max(this.activeMentionIndex - 1, 0);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        this.applyMention(this.mentionSuggestions[this.activeMentionIndex]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeMentionDropdown();
      }
      return;
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      if (this.removeMentionAcrossCaret(event)) {
        return;
      }
    }

    if (event.key === 'Enter') {
      // A CJK candidate confirmed with Enter fires this same keydown — without this guard that
      // confirmation sends whatever half-typed text was in the field before the candidate landed.
      if (event.isComposing || event.keyCode === 229) {
        return;
      }
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
        return;
      }
      // Shift+Enter inserts a newline (multiline only — a plain <input> can't hold one anyway, so
      // this is a no-op there exactly as it was before). Plain Enter and Cmd/Ctrl+Enter both send;
      // onSend() itself is a no-op on an empty/whitespace-only composer.
      if (event.shiftKey) {
        return;
      }
      event.preventDefault();
      this.onSend();
    }
  }

  /**
   * Backspace at the very start of a text segment deletes the chip before it, Delete at the very
   * end deletes the chip after it — the caret can't sit inside a chip, so without this a chip can
   * only be removed with its × button. Returns true when a chip was removed.
   */
  private removeMentionAcrossCaret(event: KeyboardEvent): boolean {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement | null;
    if (!input || (input.tagName !== 'INPUT' && input.tagName !== 'TEXTAREA')) {
      return false;
    }
    const start = input.selectionStart ?? 0;
    // A real selection is the browser's to delete, not ours.
    if (start !== (input.selectionEnd ?? 0)) {
      return false;
    }
    const backspace = event.key === 'Backspace';
    const atEdge = backspace ? start === 0 : start === input.value.length;
    if (!atEdge) {
      return false;
    }
    const partIndex = this.partIndexForTextInput(input);
    if (partIndex < 0) {
      return false;
    }
    const neighbour = backspace ? partIndex - 1 : partIndex + 1;
    if (this.composerParts[neighbour]?.kind !== 'mention') {
      return false;
    }
    // Captured before the removal, so the caret lands where the chip was rather than at the end.
    const caretOffset = this.wireOffsetBeforePart(neighbour);
    event.preventDefault();
    this.removeMentionAt(neighbour);
    this.cdr.markForCheck();
    this.focusComposerAtWireOffset(caretOffset);
    return true;
  }

  /** Inverse of textPartInputIndex: the composerParts index backing a rendered text input. */
  private partIndexForTextInput(el: HTMLInputElement | HTMLTextAreaElement): number {
    const inputIndex = (this.composerTextInputRefs?.toArray() ?? [])
      .findIndex(ref => ref.nativeElement === el);
    if (inputIndex < 0) {
      return -1;
    }
    let seen = -1;
    for (let i = 0; i < this.composerParts.length; i++) {
      if (this.composerParts[i].kind === 'text') {
        seen += 1;
        if (seen === inputIndex) {
          return i;
        }
      }
    }
    return -1;
  }

  onTextPartPaste(partIndex: number, event: ClipboardEvent): void {
    if (!this.mentionsEnabled) {
      return;
    }
    const pasted = (event.clipboardData?.getData('text/plain') ?? '')
      .replace(/\r\n/g, '\n')
      .trim();
    if (!pasted) {
      return;
    }
    event.preventDefault();
    const input = event.target as HTMLInputElement | HTMLTextAreaElement;
    const start = input?.selectionStart ?? input?.value?.length ?? 0;
    const end = input?.selectionEnd ?? start;
    const cur = this.composerParts[partIndex];
    if (!cur || cur.kind !== 'text') {
      return;
    }
    const newText = `${cur.text.slice(0, start)}${pasted}${cur.text.slice(end)}`;
    const next = this.composerParts.slice();
    next[partIndex] = { kind: 'text', text: newText };
    this.composerParts = next;
    void this.resolveAndApplyPlainMentions(
      partIndex,
      this.wireOffsetBeforePart(partIndex) + start + pasted.length,
    );
    // This path builds the new value manually (event.preventDefault() above) rather than letting
    // the browser's own paste fire a normal `input` event, so onTextPartInput's autosize call
    // never runs — do it here instead. detectChanges() (not setTimeout) flushes the [value]
    // binding synchronously first, so autosize() reads the textarea's real post-paste scrollHeight
    // rather than racing a deferred callback against Angular's own render.
    if (this.multiline && input instanceof HTMLTextAreaElement) {
      this.cdr.detectChanges();
      this.autosize(input);
    }
  }

  onTextPartInput(partIndex: number, event: Event): void {
    const input = event.currentTarget as HTMLInputElement | HTMLTextAreaElement;
    const text = input?.value ?? '';
    const next = this.composerParts.slice();
    const cur = next[partIndex];
    if (!cur || cur.kind !== 'text') {
      return;
    }
    next[partIndex] = { kind: 'text', text };
    this.composerParts = next;
    const wire = wireFromParts(this.composerParts);
    this.wireChange.emit(wire);
    const cursor = input?.selectionStart ?? text.length;
    const wireOffset = this.wireOffsetBeforePart(partIndex) + cursor;
    this.updateMentionContext(wire, wireOffset);
    if (this.multiline && input instanceof HTMLTextAreaElement) {
      this.autosize(input);
    }
  }

  /** `field-sizing: content` fallback for browsers that don't support it yet (Firefox, older
   * Safari) — resets to `auto` first so shrinking a field (deleting a wrapped line) actually
   * shrinks the box instead of only ever growing. CSS `max-height` + `overflow-y: auto` on the
   * element caps this regardless of the inline height set here. */
  private autosize(textarea: HTMLTextAreaElement): void {
    if (this.fieldSizingSupported) {
      return;
    }
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  removeMentionAt(partIndex: number): void {
    if (this.composerParts[partIndex]?.kind !== 'mention') {
      return;
    }
    const next = this.composerParts.filter((_, idx) => idx !== partIndex);
    this.composerParts = mergeAdjacentTextParts(next);
    this.wireChange.emit(wireFromParts(this.composerParts));
  }

  selectMention(suggestion: MentionSuggestion): void {
    this.applyMention(suggestion);
  }

  onSend(): void {
    if (!this.canSend) {
      return;
    }
    void this.sendResolved();
  }

  private async sendResolved(): Promise<void> {
    if (!this.canSend) {
      return;
    }
    let wire = wireFromParts(this.composerParts).trim();
    const resolved = await resolvePlainMentionsInText(wire, (label) =>
      this.mentionSearch.resolveLabelDetailed(label),
    );
    wire = resolved.text;

    if (resolved.ambiguous.length || resolved.unresolved.length) {
      this.setComposerFromWire(wire);
      const issue = resolved.ambiguous[0]
        ? {
            reason: 'ambiguous' as const,
            label: resolved.ambiguous[0].label,
            candidates: resolved.ambiguous[0].candidates,
            index: resolved.ambiguous[0].index,
          }
        : {
            reason: 'unresolved' as const,
            label: resolved.unresolved[0].label,
            index: resolved.unresolved[0].index,
          };
      this.openClarificationForMention(issue);
      this.mentionNeedsConfirm.emit({
        reason: issue.reason,
        label: issue.label,
        wire,
        clarification: issue.candidates?.every((c) => c.type === 'group')
          ? 'team'
          : 'user',
        ...(issue.candidates ? { candidates: issue.candidates } : {}),
      });
      this.cdr.markForCheck();
      return;
    }

    // Plain @mentions must not reach the gateway without structured ids.
    const leftoverPlain = findPlainDisplayMentions(wire);
    if (leftoverPlain.length) {
      this.setComposerFromWire(wire);
      const first = leftoverPlain[0];
      this.openClarificationForMention({
        reason: 'unresolved',
        label: first.label,
        index: first.index,
      });
      this.mentionNeedsConfirm.emit({
        reason: 'unresolved',
        label: first.label,
        wire,
        clarification: 'user',
      });
      this.cdr.markForCheck();
      return;
    }

    const parsed = parseMentionWire(wire);
    if (!parsed.displayText) {
      return;
    }
    this.submitComposer.emit({
      wire,
      displayText: parsed.displayText,
      gatewayMessage: parsed.gatewayMessage,
      selection: parsed.selection,
    });
    this.clear();
  }

  private async resolveAndApplyPlainMentions(
    focusPartIndex?: number,
    focusOffset?: number,
  ): Promise<void> {
    const wire = wireFromParts(this.composerParts);
    const resolved = await resolvePlainMentionsInText(wire, (label) =>
      this.mentionSearch.resolveLabelDetailed(label),
    );
    const hasWireTokens = new RegExp(MENTION_WIRE_TOKEN_RE.source).test(resolved.text);
    if (resolved.text !== wire || hasWireTokens) {
      this.setComposerFromWire(resolved.text);
      if (typeof focusPartIndex === 'number' && typeof focusOffset === 'number') {
        this.focusComposerAtWireOffset(focusOffset);
      }
    }
    this.cdr.markForCheck();
  }

  /** Focus the unresolved/ambiguous @mention and open the picker (with candidates when known). */
  private openClarificationForMention(issue: {
    reason: 'ambiguous' | 'unresolved';
    label: string;
    index: number;
    candidates?: MentionSuggestion[];
  }): void {
    const wire = wireFromParts(this.composerParts);
    const start = issue.index;
    const end = start + `@${issue.label}`.length;
    this.mentionRange = { start, end };
    if (issue.candidates?.length) {
      this.mentionSuggestions = issue.candidates;
      this.activeMentionIndex = 0;
      this.isMentionDropdownOpen = true;
      this.focusComposerAtWireOffset(end);
      return;
    }
    this.isMentionDropdownOpen = true;
    this.mentionSearch$.next(issue.label);
    this.focusComposerAtWireOffset(end);
    // Ensure active-mention context matches the plain token for applyMention.
    if (!findActiveMentionAtCursor(wire, end)) {
      // Cursor is after `@Label` — still allow applying over mentionRange.
    }
  }

  private wireOffsetBeforePart(partIndex: number): number {
    let o = 0;
    for (let j = 0; j < partIndex && j < this.composerParts.length; j++) {
      o += partWireLen(this.composerParts[j]);
    }
    return o;
  }

  private setComposerFromWire(wire: string): void {
    this.composerParts = partsFromWire(wire ?? '');
    this.wireChange.emit(wire ?? '');
  }

  private textPartInputIndex(composerPartIndex: number): number {
    let n = 0;
    for (let k = 0; k < composerPartIndex; k++) {
      if (this.composerParts[k].kind === 'text') {
        n++;
      }
    }
    return n;
  }

  private focusComposerAtWireOffset(offset: number): void {
    setTimeout(() => {
      let pos = 0;
      for (let i = 0; i < this.composerParts.length; i++) {
        const p = this.composerParts[i];
        const len = partWireLen(p);
        const end = pos + len;
        if (end >= offset || i === this.composerParts.length - 1) {
          if (p.kind === 'mention') {
            this.focusFirstTextInputAfterIndex(i);
            return;
          }
          const local = Math.min(Math.max(0, offset - pos), p.text.length);
          const refIdx = this.textPartInputIndex(i);
          const refs = this.composerTextInputRefs?.toArray() ?? [];
          const el = refs[refIdx]?.nativeElement;
          el?.focus();
          el?.setSelectionRange?.(local, local);
          return;
        }
        pos += len;
      }
    }, 0);
  }

  private focusFirstTextInputAfterIndex(fromIndex: number): void {
    for (let j = fromIndex; j < this.composerParts.length; j++) {
      if (this.composerParts[j].kind === 'text') {
        const refIdx = this.textPartInputIndex(j);
        const refs = this.composerTextInputRefs?.toArray() ?? [];
        const el = refs[refIdx]?.nativeElement;
        const tp = this.composerParts[j] as { kind: 'text', text: string };
        el?.focus();
        el?.setSelectionRange?.(tp.text.length, tp.text.length);
        return;
      }
    }
  }

  private updateMentionContext(text: string, cursor: number): void {
    if (!this.mentionsEnabled) {
      this.closeMentionDropdown();
      return;
    }

    const active = findActiveMentionAtCursor(text, cursor);
    if (!active) {
      this.closeMentionDropdown();
      return;
    }

    this.mentionRange = { start: active.start, end: active.end };
    // Held closed until the query is long enough, but the range is still tracked and the search
    // still runs, so the dropdown appears the moment the nth character lands.
    this.isMentionDropdownOpen = this.hasMinChars(active.query);
    this.mentionSearch$.next(active.query);
    this.cdr.markForCheck();
  }

  private hasMinChars(query: string): boolean {
    return (query || '').length >= this.mentionMinChars;
  }

  private closeMentionDropdown(): void {
    this.isMentionDropdownOpen = false;
    this.mentionSuggestions = [];
    this.activeMentionIndex = 0;
    this.mentionRange = null;
  }

  private applyMention(suggestion: MentionSuggestion): void {
    if (!suggestion || !this.mentionRange) {
      return;
    }
    const current = wireFromParts(this.composerParts);
    const tz = suggestion.timezone?.trim();
    const payload =
      suggestion.type === 'user' && tz ? `${suggestion.id}|${tz}` : suggestion.id;
    const token = `@${suggestion.label}<${suggestion.type}:${payload}>`;
    const after = current.slice(this.mentionRange.end);
    // A space always follows a picked mention, even with nothing typed after it yet (the common
    // case right after selecting from the dropdown) — otherwise the next word typed lands flush
    // against the chip's label with no separator, e.g. "@Layla Hassanidle time".
    const spacer = !/^\s/.test(after) ? ' ' : '';
    const nextValue = `${current.slice(0, this.mentionRange.start)}${token}${spacer}${after}`;
    this.setComposerFromWire(nextValue);
    const nextCursor = this.mentionRange.start + token.length + spacer.length;
    this.closeMentionDropdown();
    setTimeout(() => this.focusComposerAtWireOffset(nextCursor), 0);
  }
}
